import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, ArrowRight, Users, Wallet, Camera, Gamepad2, CalendarHeart, Gift } from "lucide-react";
import { api } from "../../lib/api";

const money = (n) => `$${Math.round(n || 0).toLocaleString()}`;

const stages = (j) => [
  { key: "plan", title: "Plan", steps: [
    ["Build your wedding site", j.site_ready, "/dashboard/website"], ["Add your events", j.events > 0, "/dashboard/events"],
    ["Set your budget", j.budget_items > 0, "/dashboard/budget"], ["Plan wedding party outfits", j.outfits > 0, "/dashboard/wedding-party"]] },
  { key: "invite", title: "Invite", steps: [
    ["Add your guest list", j.guests > 0, "/dashboard/guests"], ["Name your wedding party", j.party > 0, "/dashboard/wedding-party"],
    ["Collect RSVPs", j.guests > 0 && j.replied / j.guests >= 0.5, "/dashboard/rsvps"], ["Open your registry", j.registry > 0, "/dashboard/registry"]] },
  { key: "celebrate", title: "Celebrate", steps: [
    ["Set up party games", j.games > 0, "/dashboard/party-games"], ["Guests post to your feed", j.guest_posts > 0, `/site/${j.slug}/social`],
    ["Breaking-news cards shared", j.news_cards > 0, `/site/${j.slug}/news`]] },
  { key: "remember", title: "Remember", steps: [
    ["Upload your photo albums", j.photos >= 5, "/dashboard/memories"], ["Review guest photos", j.pending_photos === 0 && j.photos > 0, "/dashboard/memories"],
    ["Download your newspaper", j.news_cards > 0, "/dashboard/memories"]] },
];

export function WeddingJourney() {
  const [j, setJ] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/weddings/mine/journey").then((r) => setJ(r.data)).catch(() => {}); }, []);
  if (!j) return null;
  const st = stages(j);
  const current = st.find((s) => s.steps.some((x) => !x[1]))?.key || "remember";
  const active = open || current;
  const cards = [
    [Users, "Guests", `${j.attending} attending`, `${j.replied}/${j.guests} replied`, "/dashboard/rsvps"],
    [Wallet, "Budget", money(j.budget_paid), `of ${money(j.budget_total)} paid`, "/dashboard/budget"],
    [CalendarHeart, "Events", j.events, "on the schedule", "/dashboard/events"],
    [Gift, "Registry", j.registry, "stores & funds", "/dashboard/registry"],
    [Camera, "Photos", j.photos, `${j.guest_posts} from guests`, "/dashboard/memories"],
    [Gamepad2, "Games", j.games, "ready to play", "/dashboard/party-games"],
  ];
  return (
    <div className="space-y-6 mb-10" data-testid="wedding-journey">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {cards.map(([I, l, v, sub, to]) => (
          <Link key={l} to={to} className="bg-white border border-[var(--line)] rounded-2xl p-4 hover:border-[var(--gold)] transition-colors" data-testid={`live-card-${l.toLowerCase()}`}>
            <I className="w-5 h-5 text-[var(--gold)]" /><p className="text-2xl mt-2">{v}</p><p className="text-xs text-[var(--muted)]">{l} · {sub}</p>
          </Link>
        ))}
      </div>
      <div className="bg-white border border-[var(--line)] rounded-3xl p-5 md:p-7">
        <p className="font-serif text-2xl mb-4">Your wedding journey</p>
        <div className="grid grid-cols-4 gap-2 mb-5">
          {st.map((s, i) => {
            const done = s.steps.filter((x) => x[1]).length;
            return (
              <button key={s.key} onClick={() => setOpen(s.key)} className={`text-left rounded-2xl p-3 border transition-colors ${active === s.key ? "border-[var(--gold)] bg-[var(--bg-warm)]" : "border-[var(--line)]"}`} data-testid={`journey-stage-${s.key}`}>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)]">Step {i + 1}</p>
                <p className="font-serif text-lg md:text-xl">{s.title}</p>
                <div className="h-1.5 bg-[var(--line)] rounded-full mt-2 overflow-hidden"><div className="h-full bg-[var(--gold)]" style={{ width: `${(done / s.steps.length) * 100}%` }} /></div>
                <p className="text-[11px] text-[var(--muted)] mt-1">{done}/{s.steps.length}</p>
              </button>
            );
          })}
        </div>
        <div className="space-y-2">
          {st.find((s) => s.key === active).steps.map(([label, done, to]) => (
            <Link key={label} to={to} className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-[var(--bg-warm)] transition-colors" data-testid={`journey-step-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              {done ? <CheckCircle2 className="w-5 h-5 text-[var(--gold)]" /> : <Circle className="w-5 h-5 text-[var(--muted)]" />}
              <span className={`flex-1 text-sm ${done ? "line-through text-[var(--muted)]" : ""}`}>{label}</span>
              <ArrowRight className="w-4 h-4 text-[var(--muted)]" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
